import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Typography,
  MenuItem,
} from "@mui/material";
import { styled } from "@mui/system";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import API from "../../api";
import ConfirmDialog from "../../components/ConfirmDialog";

const Container = styled(Box)({
  padding: 16,
});

const SECTION_OPTIONS = [
  "Project Years",
  "Capacity Utilization",
  "Product Manufactured",
  "Plant & Machinery",
  "Furniture & Fixtures",
  "Other Fixed Assets",
  "Computers & Software",
  "Preliminary Expenses",
  "Consumables Expense Details",
  "Labour Cost Calculation",
  "Depreciation",
  "Cost of the Project",
  "RM Consumption",
  "Cost Of Production",
  "Power",
  "Annual Sales Realisation",
  "Working Capital Requirement",
  "Projected Profitability",
  "Break-Even Point Analysis",
  "Balance Sheet Inputs",
  "Projected Cash Flow & Promoter’s Drawings",
  "Sensitivity Analysis",
  "EMI Schedule",
];

export default function GuidelinesManagement() {
  const [guidelines, setGuidelines] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    sectionName: "",
    guide: "",
  });
  const [confirm, setConfirm] = useState({
    open: false,
    id: null,
    sectionName: "",
  });
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const show = (message, severity = "error") => {
    setSnack({ open: true, message, severity });
  };

  const load = async () => {
    try {
      const { data } = await API.get("/guidelines");
      setGuidelines(data);
    } catch {
      show("Failed to load guidelines");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleAdd = () => {
    setEditing(null);
    setForm({
      sectionName: "",
      guide: "",
    });
    setOpen(true);
  };

  const handleEdit = (g) => {
    setEditing(g);
    setForm({
      sectionName: g.sectionName || "",
      guide: g.guide || "",
    });
    setOpen(true);
  };

  const handleSave = async () => {
    try {
      if (!form.sectionName) {
        return show("Please select a section name");
      }
      if (!form.guide?.trim()) {
        return show("Please enter a guide text");
      }

      if (editing) {
        await API.put(`/guidelines/${editing._id}`, form);
        show("Updated guideline", "success");
      } else {
        await API.post("/guidelines", form);
        show("Created guideline", "success");
      }
      await load();
      setOpen(false);
    } catch (err) {
      show(err.response?.data?.message || "Save failed");
    }
  };

  const handleDeleteClick = (g) => {
    setConfirm({
      open: true,
      id: g._id,
      sectionName: g.sectionName,
    });
  };

  const confirmDelete = async () => {
    try {
      await API.delete(`/guidelines/${confirm.id}`);
      show("Deleted guideline", "success");
      await load();
    } catch {
      show("Delete failed");
    } finally {
      setConfirm({ open: false, id: null, sectionName: "" });
    }
  };

  const cancelDelete = () =>
    setConfirm({ open: false, id: null, sectionName: "" });

  const truncate = (text, len = 80) => {
    if (!text) return "";
    return text.length > len ? text.slice(0, len) + "…" : text;
  };

  return (
    <Container>
      <Typography variant="h5" gutterBottom>
        Guidelines
      </Typography>

      <Box mb={2}>
        <Button variant="contained" onClick={handleAdd} sx={{ mr: 2 }}>
          Add Guideline
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Section Name</TableCell>
              <TableCell>Guide</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {guidelines.map((g) => (
              <TableRow key={g._id}>
                <TableCell>{g.sectionName}</TableCell>
                <TableCell>{truncate(g.guide, 120)}</TableCell>
                <TableCell align="right">
                  <Button
                    size="small"
                    onClick={() => handleEdit(g)}
                    sx={{ mr: 1 }}
                  >
                    Edit
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => handleDeleteClick(g)}
                  >
                    <DeleteForeverIcon />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {guidelines.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  No guidelines found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editing ? "Edit Guideline" : "Add Guideline"}
        </DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            size="small"
            margin="dense"
            label="Section Name"
            value={form.sectionName}
            onChange={(e) =>
              setForm((f) => ({ ...f, sectionName: e.target.value }))
            }
          >
            {SECTION_OPTIONS.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            fullWidth
            size="small"
            margin="dense"
            label="Guide (instruction text)"
            value={form.guide}
            onChange={(e) => setForm((f) => ({ ...f, guide: e.target.value }))}
            multiline
            minRows={4}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete */}
      <ConfirmDialog
        open={confirm.open}
        title="Confirm Delete Guideline"
        content={`Delete guideline for “${confirm.sectionName}”? This cannot be undone.`}
        onCancel={cancelDelete}
        onConfirm={confirmDelete}
      />

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snack.severity}>{snack.message}</Alert>
      </Snackbar>
    </Container>
  );
}
